"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Zap,
  Star,
  Gift,
  Users,
  MessageCircle,
  Package,
  Clock,
  Bell,
  TrendingUp,
  Instagram,
  Shield,
  Infinity,
} from "lucide-react";

const fadeUp: any = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true as const },
  transition: { duration: 0.5, ease: "easeOut" },
};

const plans = [
  {
    id: "starter",
    name: "Starter",
    badge: null,
    monthlyPrice: 197.9,
    description: "Para e-commerces que estão estruturando o pós-venda.",
    accentColor: "text-gray-500 dark:text-gray-400",
    iconColor: "text-gray-400",
    featured: false,
    features: [
      { icon: Package, text: "Até 100 pedidos por mês" },
      { icon: MessageCircle, text: "1 número de WhatsApp + E-mail" },
      { icon: Clock, text: "Sincronização sob demanda (sem automática)" },
      { icon: TrendingUp, text: "Painel básico de Rastreio e Clientes" },
      { icon: Users, text: "1 usuário" },
    ],
  },
  {
    id: "growth",
    name: "Growth",
    badge: "Mais Escolhido",
    monthlyPrice: 297.7,
    description: "Para lojas em crescimento que querem reter e escalar.",
    accentColor: "text-[#48c1ce]",
    iconColor: "text-[#48c1ce]",
    featured: true,
    features: [
      { icon: Package, text: "Até 500 pedidos por mês" },
      { icon: Instagram, text: "WhatsApp, Instagram, Messenger e E-mail" },
      { icon: Clock, text: "Sincronização automática incluída" },
      { icon: Bell, text: "Alertas de SLA e risco de churn" },
      { icon: TrendingUp, text: "Inteligência de LTV e segmentação RFM" },
      { icon: Users, text: "Até 3 usuários" },
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    badge: "14 dias grátis",
    monthlyPrice: 497.9,
    description: "Para operações consolidadas que precisam de controle total.",
    accentColor: "text-[#ec399a]",
    iconColor: "text-[#ec399a]",
    featured: false,
    features: [
      { icon: Package, text: "Até 1.500 pedidos por mês" },
      { icon: MessageCircle, text: "3 números de WhatsApp + canais ilimitados" },
      { icon: Zap, text: "Sincronização automática incluída" },
      { icon: Star, text: "Gestão completa de influenciadores e parceiros" },
      { icon: Shield, text: "Permissões avançadas por função" },
      { icon: Infinity, text: "Equipe ilimitada" },
    ],
  },
];

function formatPrice(price: number) {
  return price.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function PricingLP3() {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#48c1ce]/30 bg-[#48c1ce]/10 text-[#48c1ce] text-xs font-bold mb-4">
            <Gift className="w-3 h-3" />
            Preços transparentes, sem surpresas
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-900 dark:text-white mb-4">
            Escolha o plano certo
            <br />
            <span className="bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">
              para o seu momento.
            </span>
          </h2>
          <p className="text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto mb-8">
            Todos os planos incluem{" "}
            <span className="font-bold text-gray-700 dark:text-gray-200">14 dias grátis</span>.
            Sem cartão de crédito. Cancele quando quiser.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 p-1.5 rounded-2xl bg-gray-100 dark:bg-white/10 border border-gray-200/60 dark:border-white/10">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-4 py-2 rounded-xl text-xs font-black transition-all duration-200 ${
                !isAnnual
                  ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-4 py-2 rounded-xl text-xs font-black transition-all duration-200 flex items-center gap-2 ${
                isAnnual
                  ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              Anual
              <span className="px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[9px] font-black">
                −20%
              </span>
            </button>
          </div>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {plans.map((plan, i) => {
            const price = isAnnual ? plan.monthlyPrice * 0.8 : plan.monthlyPrice;

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className={`relative flex flex-col rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 ${
                  plan.featured
                    ? "border-[#48c1ce] shadow-[0_0_40px_rgba(72,193,206,0.2)] bg-gradient-to-b from-[#48c1ce]/5 to-transparent dark:from-[#48c1ce]/10 md:scale-[1.03] md:z-10"
                    : "border-gray-200/70 dark:border-white/10 bg-white/80 dark:bg-white/[0.03] hover:border-gray-300 dark:hover:border-white/20"
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <div
                      className={`px-3 py-1 rounded-full text-[10px] font-black shadow-md whitespace-nowrap ${
                        plan.featured
                          ? "bg-[#48c1ce] text-white shadow-[#48c1ce]/40"
                          : "bg-amber-500 text-white shadow-amber-500/40"
                      }`}
                    >
                      {plan.featured && <Star className="inline w-2.5 h-2.5 mr-1 -mt-0.5" />}
                      {plan.badge}
                    </div>
                  </div>
                )}

                {/* Plan Name */}
                <div className="mb-5">
                  <h3 className={`text-sm font-black uppercase tracking-widest mb-1 ${plan.accentColor}`}>
                    {plan.name}
                  </h3>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 leading-relaxed">
                    {plan.description}
                  </p>
                </div>

                {/* Price — translate="no": texto troca com o toggle mensal/anual; nós traduzidos quebram o React DOM */}
                <div className="mb-6" translate="no">
                  <div className="flex items-end gap-1">
                    <span className="text-xs font-bold text-gray-400 dark:text-gray-500 mb-1">R$</span>
                    <span className="text-4xl font-black tracking-tight text-gray-900 dark:text-white tabular-nums">
                      {formatPrice(price).split(",")[0]}
                    </span>
                    <span className="text-base font-black text-gray-500 dark:text-gray-400 mb-0.5">
                      ,{formatPrice(price).split(",")[1]}
                    </span>
                  </div>
                  <div className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 mt-0.5">
                    por mês{isAnnual ? " · cobrado anualmente" : ""}
                  </div>
                </div>

                {/* CTA Button */}
                <a
                  href="#cta"
                  className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl font-black text-sm mb-6 transition-all duration-200 ${
                    plan.featured
                      ? "bg-[#48c1ce] hover:bg-[#3aafbc] text-white shadow-lg shadow-[#48c1ce]/30 hover:shadow-[#48c1ce]/50 hover:-translate-y-0.5"
                      : "border border-gray-300 dark:border-white/20 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10"
                  }`}
                >
                  {plan.featured ? (
                    <>
                      <Zap className="w-3.5 h-3.5" />
                      Começar Agora
                    </>
                  ) : (
                    "Testar Grátis"
                  )}
                </a>

                {/* Divider */}
                <div className="w-full h-px bg-gray-100 dark:bg-white/10 mb-5" />

                {/* Features */}
                <ul className="flex flex-col gap-3">
                  {plan.features.map(({ icon: Icon, text }) => (
                    <li key={text} className="flex items-start gap-2.5">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.iconColor}`} />
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 leading-relaxed">
                        {text}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center text-xs font-semibold text-gray-400 dark:text-gray-500 mt-10"
        >
          Todos os planos incluem:{" "}
          <span className="text-gray-500 dark:text-gray-400">
            Suporte por chat · Relatórios de desempenho · Atualizações automáticas
          </span>
        </motion.p>
        {/* R269 + D5 (02/10/2026): a cadência sai dos cartões e entra UMA vez, condicionada — há plano sem
            sincronização automática. O número vem de `intervaloPadraoDeSync()`, nunca escrito aqui. */}
        {intervaloSync != null && (
          <p className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 mt-2">
            Nos planos com sincronização automática, conferimos seus pedidos a cada {rotuloDoIntervalo(intervaloSync)}.
          </p>
        )}
      </div>
    </section>
  );
}
